import * as React from 'react';
import { useEffect, useState } from 'react';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import usePicTime from '@/src/Orgs/components/hooks/usePicTime';
import AreaShell from '../../chrome/AreaShell';
import { SectionCard } from '../../components/forms';
import './org.css';

/*
 * Integrations: Pic-Time. "Connect" starts the same OAuth hand-off as
 * classic; Pic-Time returns to /integrations/pic-time/redirect, whose
 * handler stores the token. There is no unlink endpoint.
 */

/* identical to classic PicTime.tsx (test host included) */
const PIC_TIME_OAUTH_URL = `https://smileboothtest.pic-time.com/oauth?responseType=accessToken&appId=AAAAAI8AAAC5kp6fo0ayQ4MaDSqN_Rt1dHgq&redirectUrl=${encodeURIComponent(
    'https://photostat.smilebooth.com/integrations/pic-time/redirect',
)}`;

type LinkState = 'loading' | 'linked' | 'unlinked' | 'unknown';

export default function OrgIntegrationsV3() {
    const { orgId } = useUserOrg();
    const { isLinkedToPictime } = usePicTime();
    const [state, setState] = useState<LinkState>('loading');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!orgId) return;
        let mounted = true;
        setState('loading');
        isLinkedToPictime()
            .then((linked) => mounted && setState(linked ? 'linked' : 'unlinked'))
            .catch(() => mounted && setState('unknown'));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, attempt]);

    const label =
        state === 'loading'
            ? 'Checking…'
            : state === 'linked'
              ? 'Connected'
              : state === 'unlinked'
                ? 'Not connected'
                : 'Couldn’t check the connection';

    return (
        <AreaShell area="org" tab="integrations" sub="Connect outside services to this organization">
            <div className="org-pg">
                <div className="stack">
                    <SectionCard title="Pic-Time" note="photo galleries">
                        <p className="org-p">
                            Pic-Time is a professional photo gallery platform for sharing galleries with clients.
                            Linking your Pic-Time account lets galleries sync automatically.
                        </p>
                        <div className="org-int">
                            <span className="state" role="status" aria-live="polite">
                                <span className={state === 'linked' ? 'pulse' : 'pulse off'} aria-hidden="true" />
                                {label}
                            </span>
                            {state === 'unknown' && (
                                <button type="button" className="tb-btn" onClick={() => setAttempt((a) => a + 1)}>
                                    Retry
                                </button>
                            )}
                            {(state === 'unlinked' || state === 'unknown') && (
                                <a className="tb-new" href={PIC_TIME_OAUTH_URL}>
                                    Connect Pic-Time
                                </a>
                            )}
                        </div>
                        {state === 'linked' && (
                            <p className="org-note">To disconnect Pic-Time, contact Smilebooth support.</p>
                        )}
                        {state === 'unlinked' && (
                            <p className="org-note">You’ll sign in to Pic-Time to approve the link.</p>
                        )}
                    </SectionCard>
                </div>
            </div>
        </AreaShell>
    );
}
